// 把一批入库的货一次性加进"入库分拣看板"（分配临时分拣仓位、累加已分拣数量）。
//
// 普通扫描入库是一件一件扫、每扫一件就分配一次仓位；退货入库初检提交后，正常入库和 AIU 的件一次性入库，
// 要在同一个事务里把它们都放进看板。逻辑和 Scan.tsx 的 addInventoryUnitToDatabase 一致：
//   1. 看板里已经有同一个库存单元的行 → 数量累加
//   2. 否则该分拣组在看板里已经有临时仓位 → 复用这个仓位，新增一行
//   3. 否则取可复用池里最小的仓位号；池子是空的才开新仓位号
// 做成不依赖 React 状态的纯函数：同一批里多个库存单元属于同一个分拣组时，后面的能看到前面刚分配的仓位。

export interface BoardRow {
  sortingGroup: string;
  sortingGroupSystemCode: string;
  inventoryUnitCode: string;
  inventoryUnitName?: string;
  temporaryLocation: number;
  sortedQuantity: number;
  productId?: number;
  displaySortingBarcode?: string;
  sortingBarcodes?: string[];
  isEmptyLocation?: boolean;
}

export interface BoardState {
  sortingDatabase: BoardRow[];
  nextLocationNumber: number;
  reusableLocationPool: number[];
}

/** 退货初检执行结果里的入库明细（后端 inbound_items） */
export interface InboundBoardItem {
  inventory_unit_barcode: string;
  unit_name?: string;
  quantity: number;
  sorting_group_name: string;
  sorting_group_system_code: string;
  sorting_barcodes?: string[];
}

export interface BoardPlacement {
  inventoryUnitCode: string;
  quantity: number;
  temporaryLocation: number;
  sortingGroupSystemCode: string;
}

export function addInboundItemsToBoard(
  state: BoardState,
  items: InboundBoardItem[],
): { state: BoardState; placements: BoardPlacement[]; skipped: InboundBoardItem[] } {
  let rows = state.sortingDatabase.map(row => ({ ...row }));
  let next = state.nextLocationNumber;
  let pool = [...state.reusableLocationPool].sort((a, b) => a - b);
  const placements: BoardPlacement[] = [];
  const skipped: InboundBoardItem[] = [];

  for (const item of items) {
    const quantity = Number(item.quantity || 0);
    const groupCode = String(item.sorting_group_system_code || '').trim();
    if (!item.inventory_unit_barcode || quantity <= 0 || !groupCode) {
      // 没有分拣组的库存单元没法在看板上分组，也就没法生成容器，交给调用方提示
      skipped.push(item);
      continue;
    }

    const sameUnitIndex = rows.findIndex(row => row.inventoryUnitCode === item.inventory_unit_barcode && row.sortingGroupSystemCode === groupCode);
    if (sameUnitIndex >= 0) {
      rows[sameUnitIndex] = { ...rows[sameUnitIndex], sortedQuantity: (rows[sameUnitIndex].sortedQuantity || 0) + quantity };
      placements.push({ inventoryUnitCode: item.inventory_unit_barcode, quantity, temporaryLocation: rows[sameUnitIndex].temporaryLocation, sortingGroupSystemCode: groupCode });
      continue;
    }

    const sameGroup = rows.find(row => row.sortingGroupSystemCode === groupCode);
    let location: number;
    if (sameGroup) {
      location = sameGroup.temporaryLocation;
    } else if (pool.length > 0) {
      location = pool[0];
      pool = pool.slice(1);
    } else {
      location = next;
      next += 1;
    }
    const codes = (item.sorting_barcodes || []).filter(Boolean);
    rows = [
      ...rows,
      {
        sortingGroup: item.sorting_group_name || '默认分拣组',
        sortingGroupSystemCode: groupCode,
        inventoryUnitCode: item.inventory_unit_barcode,
        inventoryUnitName: item.unit_name || undefined,
        temporaryLocation: location,
        sortedQuantity: quantity,
        ...(codes.length > 0 ? { displaySortingBarcode: codes[0], sortingBarcodes: codes } : {}),
      },
    ];
    placements.push({ inventoryUnitCode: item.inventory_unit_barcode, quantity, temporaryLocation: location, sortingGroupSystemCode: groupCode });
  }

  return { state: { sortingDatabase: rows, nextLocationNumber: next, reusableLocationPool: pool }, placements, skipped };
}
